/**
 * ErrorBoundary Component
 * Catches React errors and displays a fallback UI
 */

import {
  Box,
  Button,
  Flex,
  Text,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from "@chakra-ui/react";
import React, { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Update state with error info
    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      const bgColor = useColorModeValue("white", "gray.800");
      const textColor = useColorModeValue("gray.700", "white");

      return (
        <Box
          p={6}
          bg={bgColor}
          borderRadius="lg"
          border="1px solid"
          borderColor={useColorModeValue("gray.200", "gray.600")}
        >
          <Alert status="error" mb={4}>
            <AlertIcon />
            <Box>
              <AlertTitle>Something went wrong</AlertTitle>
              <AlertDescription>
                An error occurred while rendering this component.
              </AlertDescription>
            </Box>
          </Alert>

          {process.env.NODE_ENV === "development" && this.state.error && (
            <Box
              mt={4}
              p={4}
              bg={useColorModeValue("gray.50", "gray.900")}
              borderRadius="md"
              overflow="auto"
            >
              <Text fontSize="sm" fontWeight="bold" mb={2} color={textColor}>
                Error Details:
              </Text>
              <Text fontSize="xs" color="red.500" fontFamily="mono">
                {this.state.error.toString()}
              </Text>
              {this.state.errorInfo && (
                <Text fontSize="xs" color="gray.500" fontFamily="mono" mt={2}>
                  {this.state.errorInfo.componentStack}
                </Text>
              )}
            </Box>
          )}

          <Flex mt={4} gap={2}>
            <Button
              colorScheme="teal"
              onClick={this.handleReset}
            >
              Try Again
            </Button>
            <Button
              variant="outline"
              onClick={() => window.location.reload()}
            >
              Reload Page
            </Button>
          </Flex>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

